import http from "../../api/apiClient";
import type {
  Classroom,
  ClassroomFormValues,
  EntityId,
  SchoolYear,
  SchoolYearFormValues,
  Teacher,
  TeacherAssignment,
  TeacherFormValues,
  TeacherRole,
  Subject,
} from "./types";

const asArray = <T,>(value: T[] | unknown): T[] => (Array.isArray(value) ? value : []);

export const listTeachers = async (): Promise<Teacher[]> => {
  const { data } = await http.get<Teacher[]>("/teachers");
  return asArray<Teacher>(data);
};

export const getTeacher = async (id: EntityId): Promise<Teacher> => {
  const { data } = await http.get<Teacher>(`/teachers/${id}`);
  return data;
};

export const createTeacher = async (payload: TeacherFormValues): Promise<Teacher> => {
  const { data } = await http.post<Teacher>("/teachers", {
    firstName: payload.firstName,
    lastName: payload.lastName,
    address: payload.address,
    zipCode: payload.zipCode,
    city: payload.city,
    phone: payload.phone,
    email: payload.email,
    dateBirth: payload.dateBirth,
    capabilities: payload.capabilities,
    subjects: payload.subjectIds.map((id) => ({ id })),
  });
  return data;
};

export const updateTeacher = async (id: EntityId, payload: TeacherFormValues): Promise<Teacher> => {
  const { data } = await http.put<Teacher>(`/teachers/${id}`, {
    firstName: payload.firstName,
    lastName: payload.lastName,
    address: payload.address,
    zipCode: payload.zipCode,
    city: payload.city,
    phone: payload.phone,
    email: payload.email,
    dateBirth: payload.dateBirth,
    capabilities: payload.capabilities,
    subjects: payload.subjectIds.map((subjectId) => ({ id: subjectId })),
  });
  return data;
};

export const updateTeacherContactSharing = async (id: EntityId, payload: TeacherFormValues): Promise<Teacher | null> => {
  const { data } = await http.patch<Teacher | null>(`/teachers/${id}/contact-sharing`, {
    sharePhone: Boolean(payload.sharePhone),
    shareEmail: Boolean(payload.shareEmail),
    shareAddress: Boolean(payload.shareAddress),
  });
  return data ?? null;
};

export const deleteTeacher = async (id: EntityId): Promise<void> => {
  await http.delete(`/teachers/${id}`);
};

export const linkTeacherSelf = async (id: EntityId): Promise<Teacher | null> => {
  const { data } = await http.patch<Teacher | null>(`/teachers/${id}/link-self`);
  return data ?? null;
};

export const unlinkTeacherUser = async (id: EntityId): Promise<void> => {
  await http.patch(`/teachers/${id}/unlink-user`);
};

export const listTeacherClassrooms = async (teacherId: EntityId): Promise<TeacherAssignment[]> => {
  const { data } = await http.get<TeacherAssignment[]>(`/teacher-classrooms/teacher/${teacherId}`);
  return asArray<TeacherAssignment>(data);
};

export const listSchoolYears = async (): Promise<SchoolYear[]> => {
  const { data } = await http.get<SchoolYear[]>("/years");
  return asArray<SchoolYear>(data);
};

export const getSchoolYear = async (id: EntityId): Promise<SchoolYear> => {
  const { data } = await http.get<SchoolYear>(`/years/${id}`);
  return data;
};

export const createSchoolYear = async (payload: SchoolYearFormValues): Promise<SchoolYear> => {
  const { data } = await http.post<SchoolYear>("/years", {
    startDate: payload.startDate,
    endDate: payload.endDate,
    selfSignupDeadline: payload.selfSignupDeadline || null,
  });
  return data;
};

export const updateSchoolYear = async (id: EntityId, payload: SchoolYearFormValues): Promise<SchoolYear> => {
  const { data } = await http.put<SchoolYear>(`/years/${id}`, {
    startDate: payload.startDate,
    endDate: payload.endDate,
    defaultMonthlyFee: payload.defaultMonthlyFee === "" ? null : Number(payload.defaultMonthlyFee),
    selfSignupDeadline: payload.selfSignupDeadline || null,
  });
  return data;
};

export const deleteSchoolYear = async (id: EntityId): Promise<void> => {
  await http.delete(`/years/${id}`);
};

export const listClassrooms = async (): Promise<Classroom[]> => {
  const { data } = await http.get<Classroom[]>("/classrooms");
  return asArray<Classroom>(data);
};

export const getClassroom = async (id: EntityId): Promise<Classroom> => {
  const { data } = await http.get<Classroom>(`/classrooms/${id}`);
  return data;
};

export const updateClassroom = async (id: EntityId, payload: ClassroomFormValues): Promise<Classroom> => {
  const { data } = await http.put<Classroom>(`/classrooms/${id}`, {
    className: payload.className,
    schoolYearId: payload.schoolYearId || null,
    teacherId: null,
    grade: payload.grade ? Number(payload.grade) : null,
    maxChildren: payload.maxChildren ? Number(payload.maxChildren) : null,
  });
  return data;
};

export const createKindergartenClassrooms = async (schoolYearId: EntityId, count: number): Promise<Classroom[]> => {
  const { data } = await http.post<Classroom[]>("/classrooms/kindergarten/batch", {
    schoolYearId,
    count,
  });
  return asArray<Classroom>(data);
};

export const createGradeClassroom = async (
  className: string,
  schoolYearId: EntityId,
  teacherId: EntityId | null,
  grade: number | null
): Promise<Classroom> => {
  const { data } = await http.post<Classroom>("/classrooms", {
    className,
    schoolYearId,
    teacherId,
    classType: "GRADE",
    grade,
  });
  return data;
};

export const deleteClassroom = async (id: EntityId): Promise<void> => {
  await http.delete(`/classrooms/${id}`);
};

export const listClassroomTeacherAssignments = async (classroomId: EntityId): Promise<TeacherAssignment[]> => {
  const { data } = await http.get<TeacherAssignment[]>(`/teacher-classrooms/classroom/${classroomId}`);
  return asArray<TeacherAssignment>(data);
};

export const assignTeacherToClassroom = async (
  teacherId: EntityId,
  classroomId: EntityId,
  role: TeacherRole,
  subjectIds: EntityId[] = []
): Promise<TeacherAssignment> => {
  const today = new Date().toISOString().split("T")[0];
  const { data } = await http.post<TeacherAssignment>("/teacher-classrooms", {
    teacherId,
    classroomId,
    role,
    subjectIds,
    startDate: today,
  });
  return data;
};

export const listSubjects = async (): Promise<Subject[]> => {
  const { data } = await http.get<Subject[]>("/subjects");
  return asArray<Subject>(data);
};

export const getSubject = async (id: EntityId): Promise<Subject> => {
  const { data } = await http.get<Subject>(`/subjects/${id}`);
  return data;
};

export const createSubject = async (payload: Omit<Subject, "id">): Promise<Subject> => {
  const { data } = await http.post<Subject>("/subjects", payload);
  return data;
};

export const updateSubject = async (id: EntityId, payload: Omit<Subject, "id">): Promise<Subject> => {
  const { data } = await http.put<Subject>(`/subjects/${id}`, payload);
  return data;
};

export const archiveSubject = async (id: EntityId): Promise<Subject> => {
  const { data } = await http.delete<Subject>(`/subjects/${id}`);
  return data;
};

export const unassignTeacherFromClassroom = async (assignmentId: EntityId): Promise<void> => {
  const today = new Date().toISOString().split("T")[0];
  await http.put(`/teacher-classrooms/${assignmentId}`, { endDate: today });
};
